import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useDashboardFederationsControllerCount } from "@/api/__generated__/dashboard-federations/dashboard-federations";
import {
  AvailablePlatformAsyncInlineCommand,
  CurrentPlatformLogo,
  PlatformLogo,
} from "@/components/platform";
import { useCurrentPlatform, useCurrentUser, useSwitchPlatform, useTourContext } from "@/contexts";
import { useCanViewPlatformSettings, useGetPlatformSettingsHref, useScopedHref } from "@/hooks";
import { usePrefetchOnHover } from "@/hooks/common/usePrefetchOnHover";
import { useT } from "@/i18n";
import { trackAction } from "@/lib/sentry";
import {
  ButtonLink,
  IconPlus,
  IconButtonLink,
  DialogTrigger,
  PopoverOrTray,
  IconSettings,
} from "@/ui";

import { GlobalSelectButton } from "../GlobalSelectButton";
import { useGetNavLinkPath } from "../NavLink";

export const PlatformSelect = () => {
  const t = useT();
  const switchPlatform = useSwitchPlatform();
  const scopedHref = useScopedHref();
  const getNavLinkPath = useGetNavLinkPath();
  const currentPlatform = useCurrentPlatform();
  const currentUser = useCurrentUser();
  const canCreatePlatform = currentUser.role === "superAdmin";
  // The settings gear only needs view access — the update-platform page
  // renders read-only without `PLATFORM_SETTINGS:WRITE`.
  const canViewPlatformSettings = useCanViewPlatformSettings();
  const getPlatformSettingsHref = useGetPlatformSettingsHref();
  const { data: federationCountData } = useDashboardFederationsControllerCount({
    platformIds: [currentPlatform.id],
  });
  const subOrganizationsCount = federationCountData?.total ?? 0;
  const [isOpen, setIsOpen] = useState(false);
  const tourContext = useTourContext();

  const excludePlatformIds = useMemo(() => [currentPlatform.id], [currentPlatform.id]);

  const createPlatformHref = scopedHref("/platforms/new");
  const organizationsHref = getNavLinkPath("organizations");
  // Platform settings open in the platform-only scope (org segment cleared) —
  // see `useGetPlatformSettingsHref` — not at the current org's scoped path.
  const platformSettingsHref = getPlatformSettingsHref(currentPlatform);
  const prefetchOrganizations = usePrefetchOnHover(organizationsHref);
  const prefetchPlatformSettings = usePrefetchOnHover(platformSettingsHref);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <GlobalSelectButton
        id={tourContext.ids.platformSelect}
        title={currentPlatform.title}
        className="w-full md:max-w-[231px]"
        subtitle={t("platform.components.globalSelect.subtitle")}
        icon={<PlatformLogo platform={currentPlatform} height={20} width={20} />}
      />
      <PopoverOrTray
        popoverProps={{
          className: "p-3 md:w-[262px]",
        }}
      >
        {({ isTray }) => (
          <>
            <div className="flex gap-3 items-start w-full">
              <div className="flex items-center shrink-0 mt-2">
                <CurrentPlatformLogo height={25} width={25} />
              </div>
              <div className="flex flex-col flex-1 items-stretch">
                <span className="preset-title-md text-new-primary flex-1 text-wrap">
                  {currentPlatform.title}
                </span>

                <Link
                  to={organizationsHref}
                  onClick={() => setIsOpen(false)}
                  onPointerEnter={prefetchOrganizations}
                  onFocus={prefetchOrganizations}
                  className="preset-body-sm text-secondary underline underline-offset-2 cursor-pointer hover:opacity-hover"
                >
                  {t("platform.components.globalSelect.subOrganizationsCount", {
                    count: subOrganizationsCount - 1,
                  })}
                </Link>
              </div>
              {canViewPlatformSettings && (
                <div className="flex items-center">
                  <IconButtonLink
                    icon={IconSettings}
                    href={platformSettingsHref}
                    onPress={() => setIsOpen(false)}
                    onHoverStart={prefetchPlatformSettings}
                    onFocus={prefetchPlatformSettings}
                    label={t("platform.actions.settings")}
                    size="sm"
                    className="text-new-primary"
                    hideTooltip={true}
                  />
                </div>
              )}
            </div>
            <AvailablePlatformAsyncInlineCommand
              excludePlatformIds={excludePlatformIds}
              autoFocus={!isTray}
              className="mt-2.5"
              menuProps={{
                style: !isTray
                  ? {
                      height: "140px",
                      maxHeight: "140px",
                      minHeight: "140px",
                    }
                  : undefined,
                className: isTray ? "flex-[1_1_0] overflow-y-auto" : undefined,
              }}
              onOptionAction={(option) => {
                if (!option) return;
                trackAction("dashboard.sidebar.platform.change", {
                  fromPlatformId: currentPlatform.id,
                  toPlatformId: option.id,
                });
                switchPlatform(option.id);
                setIsOpen(false);
              }}
            />
            {canCreatePlatform && (
              <ButtonLink
                href={createPlatformHref}
                startIcon={IconPlus}
                variant="tertiary"
                color="brand"
                className="w-full mt-2.5"
                onPress={() => {
                  trackAction("dashboard.sidebar.platform.create", {
                    fromPlatformId: currentPlatform.id,
                  });
                  setIsOpen(false);
                }}
              >
                {t("platform.actions.create")}
              </ButtonLink>
            )}
          </>
        )}
      </PopoverOrTray>
    </DialogTrigger>
  );
};
